import React, { useContext, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { useStepContext } from '~/App/Components/Typeform';
import HoverableTooltip from '~/components/Tooltips/HoverableTooltip';
import useTypeformNavContext from '~/Share/TestimonialLandingPage/Context/TypeformNav';
import { SpinnerNew as Spinner } from '~/App/Components/Shared/Spinner';
import { useTranslation } from 'react-i18next';
import RespondentContext from '~/Share/TestimonialLandingPage/Context/Respondent';
import TestimonialContext from '../../Context/TestimonialContext';
import { RichTextViewer } from '@vimily/bonjoro-ui-components-v2/RichTextViewer';
import { defaultTestimonialText } from '../../Pages/Editor/constants';
import { getRichTextFromSlateData } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { Checkbox } from '@vimily/bonjoro-ui-components-v2/Checkbox';
import { computeTextColor } from '@vimily/bonjoro-ui-components-v2';

export interface ConsentCardProp {
  checked: boolean;
  loading?: boolean;
  onCheckboxClicked: () => void;
  onSubmitClick?: () => void;
  className?: string;
}

export default function ConsentCard({
  checked,
  loading,
  onCheckboxClicked,
  onSubmitClick,
  className,
}: ConsentCardProp) {
  const { nextStep, previousStep, isCurrent } = useStepContext();
  const { setNavBarProps } = useTypeformNavContext();

  const { t } = useTranslation();

  const { respondent } = useContext(RespondentContext);
  const testimonial = useContext(TestimonialContext);
  const { text } = testimonial;
  const questions = respondent.getQuestions();
  const requests = respondent.getRequests();

  const questionsResponded = useMemo(
    () => questions.find((question) => !!question?.getResponse()),
    [questions]
  );

  const requestAssetsAdded = useMemo(
    () => requests.get('files').find((file) => !!file?.getResponse()),
    [requests]
  );

  const {
    font_family,
    closing_message,
    cta_corner_radius = 0,
    primary_color,
  } = testimonial?.metadata?.style ?? {};

  const canSubmit = checked && !!(questionsResponded || requestAssetsAdded);

  const handleSubmit = () => {
    onSubmitClick?.();
    nextStep(true);
  };

  useEffect(() => {
    if (!isCurrent) return;

    setNavBarProps(true, false, handleSubmit, previousStep);
  }, [isCurrent, checked]);

  const buttonProps = {
    className: 'ml-auto px-4 h-10 py-1.5',
    variant: ButtonVariant.Primary,
    onClick: handleSubmit,
    disabled: !canSubmit,
    style: {
      borderRadius: `${cta_corner_radius}px`,
      fontFamily: font_family,
      ...(canSubmit
        ? {
            borderColor: primary_color,
            backgroundColor: primary_color,
            color: computeTextColor(primary_color),
          }
        : {}),
    },
  };

  const bgColor = closing_message?.box_color;
  const textColor = computeTextColor(bgColor);

  return (
    <div
      className={cx(className, [
        'w-full p-10 overflow-hidden justify-center',
        'flex flex-col items-center md:rounded-2xl lg:w-[817px] md:h-fit min-h-[400px] lg:min-w-[818px]',
      ])}
      style={{ backgroundColor: bgColor }}
    >
      <div className='flex flex-col items-center'>
        <RichTextViewer
          className='text-center w-fit'
          value={getRichTextFromSlateData(
            text.closing_message_rich ??
              defaultTestimonialText.closing_message_rich
          )}
          style={{ fontFamily: font_family, color: textColor }}
        />
        <div className='flex items-center justify-center py-5 px-4 text-sm'>
          <Checkbox
            className='bg-white rounded mr-3'
            checked={checked}
            onChange={onCheckboxClicked}
          />
          <RichTextViewer
            className='items-center w-fit'
            value={getRichTextFromSlateData(
              text.consent_statement_rich ??
                defaultTestimonialText.consent_statement_rich
            )}
            style={{ fontFamily: font_family, color: textColor }}
          />
        </div>
        <div>
          <HoverableTooltip
            id='consent-card-tooltip'
            content={t('testimonial:consent.submitTooltip.noRespond')}
            disableTooltip={canSubmit}
          >
            <Button {...buttonProps}>
              {t('testimonial:consent.submit')}{' '}
              {loading && <Spinner className='ml-2' />}
            </Button>
          </HoverableTooltip>
        </div>
      </div>
    </div>
  );
}
